import LeftArrowIcon from "../../../../../../../../assets/svg/left-arrow-icon";
import UserRoleIcon from "../../../../../../../../assets/svg/user-role-icon";
import ChevronIcon from "../../../../../../../../assets/svg/chevron-icon";
import Dropdown from "../../../../../../../../components/Dropdown/Dropdown";
import { useMemo, useState } from "react";
import { ROLE_CONFIG } from "../../../../../RolesLayout/utils/constants";
import { visibleScrollbar } from "../../../../../../../../utils/functions/get-visible-scrollbar";
import { useGetValidRolesForUserCreationQuery } from "../../../../../../../../redux/services/userManagementApi";
import { RoleDetails } from "../../../../../RolesLayout/utils/types";
import { AssignRoleValues } from "../../../utils/types";
import { FormikProps } from "formik";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../../../redux/store";
import {
  CREATE_USER_ASSIGN_ROLE_HEIGHTS,
  ROLE_HIERARCHY,
} from "../../../../../../utils/constants";
import { TruncatedTextTooltip } from "../../../../../../../../components/TruncatedTextTooltip/TruncatedTextTooltip";
import { PropertyGroupSelector } from "./components/PropertyGroupSelector";

interface AssignRoleProps {
  name: string;
  setCreateUserStep: (step: number) => void;
  formik: FormikProps<AssignRoleValues>;
}

export const AssignRole = ({
  name,
  setCreateUserStep,
  formik,
}: AssignRoleProps) => {
  const { data: roleDetails, isLoading: isRoleDetailsLoading } =
    useGetValidRolesForUserCreationQuery({});

  const auth = useSelector((state: RootState) => state?.auth);
  const fontSize = useSelector(
    (state: RootState) => state?.fontSettings?.fontSize
  );

  const [showPermissions, setShowPermissions] = useState<boolean>(false);

  const assignRoleHeight =
    CREATE_USER_ASSIGN_ROLE_HEIGHTS[
      fontSize as keyof typeof CREATE_USER_ASSIGN_ROLE_HEIGHTS
    ];

  const selectedRole = formik?.values?.role;

  const handleBack = () => {
    setCreateUserStep(1);
  };

  const handleViewPermissions = () => {
    setShowPermissions(!showPermissions);
  };

  const handleSelectRole = (value: string) => {
    if (value && formik?.values?.role === value) {
      formik?.setFieldValue("role", null);
    } else {
      formik?.setFieldValue("role", value);
    }
  };

  const selectedRolePermissions = roleDetails?.find(
    (item: RoleDetails) => item?.role === selectedRole
  )?.permissions?.[0];

  const availableRoles = useMemo(() => {
    const currentUserRole = auth?.user?.role?.toLowerCase() || "viewer";
    return roleDetails
      ?.filter(
        (item: RoleDetails) =>
          ROLE_HIERARCHY[item?.role as keyof typeof ROLE_HIERARCHY] <=
          (ROLE_HIERARCHY[currentUserRole as keyof typeof ROLE_HIERARCHY] || 0)
      )
      ?.map((item: RoleDetails) => ({
        value: item?.role,
        content: (
          <div className="flex items-center gap-1.5">
            <div
              className={`w-2 h-2 rounded-full ${
                ROLE_CONFIG[item?.role as keyof typeof ROLE_CONFIG]?.bgColor
              }`}
            />
            <span className="overflow-hidden text-ellipsis whitespace-nowrap min-w-0 text-sm text-heading-text dark:text-heading-text-dk">
              {ROLE_CONFIG[item?.role as keyof typeof ROLE_CONFIG]?.label}
            </span>
          </div>
        ),
      }));
  }, [roleDetails, auth]);

  return (
    <div className="flex flex-col h-full">
      <button
        onClick={handleBack}
        className="flex items-center gap-1 py-[18px]"
      >
        <LeftArrowIcon className="w-3.5 h-3.5 text-verba-indigo dark:text-verba-indigo-dk" />
        <span className="text-xs font-medium text-heading-text dark:text-heading-text-dk">
          Go Back
        </span>
      </button>

      <div className="flex items-start gap-2">
        <UserRoleIcon className="w-6 h-6 shrink-0 text-verba-blue dark:text-verba-blue-dk" />
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1 text-base text-heading-text dark:text-heading-text-dk leading-normal">
            <span>Assign</span>
            <TruncatedTextTooltip
              text={name}
              maxWidth="max-w-[200px] inline-block truncate font-semibold"
            >
              {name}
            </TruncatedTextTooltip>
            <span>Role and Permissions</span>
          </div>
          <p className="text-md text-dark-gray dark:text-dark-gray-dk leading-normal">
            Roles control what a user can do. Property groups control where,
            from whole countries down to single properties.
          </p>
        </div>
      </div>

      <div
        className={`overflow-y-auto pr-1 ${assignRoleHeight} ${visibleScrollbar(
          "[&::-webkit-scrollbar]:w-[8px]"
        )}`}
      >
        <Dropdown
          id="role-dropdown"
          label="Assign Role"
          options={isRoleDetailsLoading ? [] : availableRoles}
          value={selectedRole}
          onChange={handleSelectRole}
          placeholder={isRoleDetailsLoading ? "Loading roles…" : "Select role"}
          className="w-[264px] mt-9"
          triggerClassName="py-[2px]"
          selectedOptionContainerClassName="w-full pr-3"
          placeholderClassName="text-gray-placeholder dark:text-gray-placeholder-dk text-md font-normal"
          labelClassName="text-md text-verba-field-label dark:text-verba-field-label-dk"
        />
        {formik?.touched?.role && formik?.errors?.role && (
          <div className="text-xxs text-verba-red-alert dark:text-verba-red-alert-dk mt-[3px]">
            {formik?.errors?.role}
          </div>
        )}
        {selectedRole && (
          <div className="w-max flex gap-1 cursor-pointer select-none mt-3">
            <button
              onClick={handleViewPermissions}
              className="flex items-center gap-1"
            >
              <span className="text-md text-verba-blue dark:text-verba-blue-dk">
                {showPermissions
                  ? "Hide Role Permissions"
                  : "View Role Permissions"}
              </span>
              <ChevronIcon
                className={`w-3.5 h-3.5 text-verba-blue dark:text-verba-blue-dk ${
                  showPermissions ? "-rotate-180" : ""
                }`}
              />
            </button>
          </div>
        )}

        {selectedRole && showPermissions && (
          <>
            <div className="mt-[18px] mb-2 flex items-center gap-2">
              <div
                className={`w-2 h-2 rounded-full ${
                  ROLE_CONFIG[selectedRole as keyof typeof ROLE_CONFIG]?.bgColor
                }`}
              />
              <span className="text-verba-indigo dark:text-verba-indigo-dk text-base font-semibold">
                {ROLE_CONFIG[selectedRole as keyof typeof ROLE_CONFIG]?.label}{" "}
                Role Permissions
              </span>
              <span className="h-[18px] px-1 flex items-center justify-center text-xxs font-semibold leading-0 text-verba-indigo dark:text-verba-indigo-dk rounded-sm border border-gray-100 dark:border-gray-100-dk">
                {selectedRolePermissions?.count} Permissions
              </span>
            </div>
            <div>
              {selectedRolePermissions?.permissions?.map(
                (permission: string, index: number) => {
                  return (
                    <div
                      key={index}
                      className={`flex flex-col gap-1 py-3 ${
                        index ===
                        selectedRolePermissions?.permissions?.length - 1
                          ? "border-b-0"
                          : "border-b border-border-gray dark:border-border-gray-dk"
                      }`}
                    >
                      <span className="text-md text-verba-indigo dark:text-verba-indigo-dk font-medium">
                        {permission}
                      </span>
                    </div>
                  );
                }
              )}
            </div>
          </>
        )}

        <PropertyGroupSelector formik={formik} />
      </div>
    </div>
  );
};
